<template>
    <div>
        <app-layout id="payment_behalf">
            <template #header>
                <h2 class="title">
                    Make Payment
                    <span v-if="for_user">On Behalf of {{ for_user.first_name }} {{ for_user.last_name }}</span>
                </h2>
                <flash-messages />
            </template>

            <section class="py-2">
                <div class="bg-white p-3">
                    <div class="row">
                        <div class="col-sm-5">
                            <SelectCard :cards="cards" :form="form" @setPaymentMethod="setPaymentMethod" />

                            <SelectAccount :banks="banks" :link_token="link_token" :form="form" bankRoute="cart.addBank"
                                :form_bank="formBank" @setPaymentMethod="setPaymentMethod" />

                            <div class="mt-3 pt-3 border-top" v-if="for_user">
                                <h3>Cash Payment</h3>
                                <div v-if="this.$page.props.auth.user.drawer">
                                    <p class="mb-2 d-flex">
                                        <strong>Due</strong>
                                        <span class="ml-2">{{ formatDollars(this.payment.total_amount) }}</span>
                                    </p>
                                    <p class="m-0 d-flex">
                                        <strong>Tendered</strong>
                                        <input type="text" class="form-control cash-tendered ml-3"
                                            v-model="formCash.credit_amount" size="4" @input="calcCashChange" />
                                    </p>
                                    <div v-if="formCash.debit_amount !== null">
                                        <div class="row">
                                            <div class="col-sm-4">
                                                <strong>Change</strong>
                                            </div>
                                            <div class="col-sm-8">
                                                {{ formatDollars(formCash.debit_amount) }}
                                            </div>
                                        </div>
                                    </div>
                                    <button class="btn btn-success mt-3" @click="cashConfirm" v-if="!cash">Confirm Cash
                                        Payment</button>
                                </div>
                                <div class="starting-balance" v-else>
                                    Starting Balance:
                                    <input type="text" class="form-control ml-2" placeholder="$"
                                        v-model="formCash.amount" />
                                    <p @click="cashOpen" class="text-success cursor-pointer m-0 ml-2">
                                        <i class="fas fa-plus mr-1"></i>Open Cash Drawer
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="bottom-button">
                        <jet-button class="px-5" btnStyle="success" icon="fas fa-shopping-cart" :disable="form.processing"
                            @click="completeCheckout()">Submit</jet-button>
                    </div>
                </div>
            </section>

            <section class="my-3 p-3 bg-white">
                <p class="mb-3"><i class="fas fa-calendar-alt fa-lg mr-2 text-primary"></i>{{ this.items.length }} Payments
                    Due On: {{ moment.utc(this.payment.due_date).format('M/D/YY') }}</p>

                <div v-for="payment in items" :key="payment.id"
                    class="py-2 border-top border-bottom d-flex align-items-baseline flex-column flex-lg-row">
                    <p class="m-0 mr-3 pr-3 font-weight-bold">Payment {{ payment.number }} of {{ payment.total }}</p>
                    <p class="m-0 mr-3 pr-3 text-size-small"><em>Order placed on {{
                        moment(payment.order.created_at).format('M/D/YY') }} for {{ formatDollars(payment.order.total)
    }}</em></p>
                    <inertia-link class="mr-3 pr-3" :href="orderRoute(payment)">Order #{{ payment.order.id }}</inertia-link>
                    <p class="m-0 mr-3 pr-3">Payment Amount: <strong class="ml-2">{{ formatDollars(payment.payment_amount)
                    }}</strong></p>
                </div>

                <p class="m-0 mt-2 font-weight-bold">
                    Total: <span class="ml-2">{{ formatDollars(this.payment.total_amount) }}</span>
                </p>
            </section>

            <AddCard :routeUrl="route('cart.addCard')" :formCard="formCard" />
        </app-layout>
    </div>
</template>

<script>
import AppLayout from '@/Layouts/AppLayout'
import FlashMessages from '@/Shared/FlashMessages'

import moment from 'moment'

import AddCard from '@/Components/Modals/AddCard'
import SelectCard from '@/Components/Payment/SelectCard'
import SelectAccount from '@/Components/Payment/SelectAccount'

export default {
    components: {
        AppLayout,
        FlashMessages,
        AddCard,
        SelectCard,
        SelectAccount
    },
    props: {
        for_user: Object,
        order: Object,
        payment: Object,
        payment_types: Array,
        cards: Array,
        banks: Array,
        order_total: Number,
        link_token: String,
        balance: Number,
        items: Array,
        payment_automatic: Number,
        payment_method_id: String,
        available_credit: Number,
        payment_balance_total: Number,
        cash: Object
    },
    data() {
        return {
            form: this.$inertia.form({
                payment_intent: this.cash ? 'Cash' : 'Credit Card',
                stripe_payment_method: this.payment_method_id,
                payment_id: this.payment.id,
                full_balance: 0,
                payment_automatic: this.payment_automatic,
            }),
            formCard: this.$inertia.form({
                payment_id: this.payment.id,
                card_name: null,
                card_number: null,
                card_exp_month: null,
                card_exp_year: null,
                card_cvc: null,
                card_zip: null,
            }),
            formBank: this.$inertia.form({
                payment_id: this.payment.id,
                public_token: null,
                account_id: null
            }),
            formCash: this.$inertia.form({
                payment_id: this.payment.id,
                amount: null,
                payment_amount: this.order_total,
                credit_amount: this.cash ? this.cash.credit_amount : null,
                debit_amount: this.cash ? this.cash.debit_amount : null,
            }),
        }
    },
    created() {
        this.moment = moment;
    },
    methods: {
        orderRoute(payment) {
            return route('order.view', payment.order.id)
        },
        setPaymentMethod(e, id) {
            if (e.target.checked) this.form.stripe_payment_method = id;
        },
        toggleFullBalance(e) {
            if (e.target.checked) {
                this.form.full_balance = 1;
            } else {
                this.form.full_balance = 0;
            }
        },
        completeCheckout() {
            this.form.apply_account_balance = this.apply_account_balance ? 1 : 0;

            if ((this.form.payment_intent == 'Credit Card' ||
                this.form.payment_intent == 'ACH') &&
                !this.form.stripe_payment_method) {
                // display error
            } else {
                this.form.post(this.route('tu.payment.submit'));
            }
        },
        cashOpen() {
            this.formCash.post(this.route('mtd.cash.open'));
        },
        calcCashChange() {
            if (this.formCash.credit_amount >= this.payment.total_amount) {
                this.formCash.debit_amount = this.formCash.credit_amount - this.payment.total_amount;
            } else {
                this.formCash.debit_amount = null;
            }
        },
        cashConfirm() {
            if (this.formCash.credit_amount >= this.payment.total_amount) {
                this.formCash.post(this.route('mtd.cash.confirm'));
            }
        },
    },
}
</script>

<style>
.gallery-thumbnail img {
    width: 120px;
}

.starting-balance {
    display: flex;
    align-items: center;
}

input.cash-tendered,
.starting-balance input {
    width: 75px;
    height: 25px;
}
</style>
